import { IAgentQuoteData } from 'Feature/PageContent/Models/MyInsightsModel';
import { IUserData } from 'Foundation/User/UserContextModels';
import states from 'Data/states.json';
import { IAgencyMetrics } from 'Foundation/Agency/IAgencyMetrics';
import { StringHelper } from 'Utilities/StringHelper';
import * as defaultAgentQuoteData from 'Data/defaultAgentQuoteData.json';
import * as defaultAgencyQuoteData from 'Data/defaultAgencyQuoteData.json';

/**
 * High-performance token replacement.
 * Previous implementation performed ~O(T * N) string scans (T = number of token keys, N = doc length)
 * by calling replaceAll for every individual token (300+). This refactor collapses that
 * into (1) building a token map, (2) a single regex pass for all {token} style values,
 * and a handful of targeted regex passes for the function-style ($format_*, $percentage, $state) tokens.
 */
export const replaceUserTokens = (
  doc?: string,
  userData?: IUserData,
  agentQuoteData?: IAgentQuoteData | null,
  agencyMetrics?: IAgencyMetrics | null,
  userCampaigns?: Record<string, any>[] | null,
) => {
  if (!doc || !userData) return doc ?? '';
  try {
    // 1. Build static token map (curly brace tokens only)
    const tokenMap: Record<string, string> = {};

    if (agentQuoteData) populateAgentTokens(tokenMap, agentQuoteData);
    if (agencyMetrics) populateAgencyTokens(tokenMap, agencyMetrics);
    if (userCampaigns) populateCampaignTokens(tokenMap, userCampaigns);

    // Custom constant tokens
    tokenMap['yesterday'] = getYesterdayDate();

    // Name tokens (handled differently for employees to preserve spacing logic)
    const isAgent = userData.isAgent() || userData.isMockingAgent;
    if (isAgent) {
      tokenMap['firstName'] = userData.firstName();
      tokenMap['lastName'] = userData.lastName();
    }

    // 2. Fast curly-brace replacement using a single regex pass
    if (/{/.test(doc) && Object.keys(tokenMap).length) {
      const tokenRegex = buildTokenRegex(Object.keys(tokenMap));
      doc = doc.replace(tokenRegex, (_, key: string) => tokenMap[key] ?? _);
    }

    // 3. Post-process name tokens for employees (non-agents) to match legacy behavior
    if (!isAgent) {
      if (doc.includes('{firstName} {lastName}'))
        doc = doc.replaceAll('{firstName} {lastName}', 'Liberty Mutual employee');
      if (doc.includes('{firstName}')) doc = doc.replaceAll('{firstName}', 'Liberty Mutual employee');
      if (doc.includes('{lastName}')) doc = doc.replaceAll('{lastName}', '');
    }

    // 4. Functional tokens (dates, percentages, state codes)
    doc = formatFunctionalTokens(doc);
  } catch (err) {
    // Fail safe: never throw upstream from token replacement.
    console.error(err);
  }
  return doc ?? '';
};

// ------------------- Internal Helpers ------------------- //

const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Cache compiled regex per key set to avoid rebuilding on every invocation.
const tokenRegexCache: Record<string, RegExp> = {};
const buildTokenRegex = (keys: string[]): RegExp => {
  const sorted = [...keys].sort(); // stable cache key irrespective of insertion order
  const cacheKey = sorted.join('|');
  if (tokenRegexCache[cacheKey]) return tokenRegexCache[cacheKey];
  const pattern = sorted.map(escapeRegex).join('|');
  const regex = new RegExp(`{(${pattern})}`, 'g');
  tokenRegexCache[cacheKey] = regex;
  return regex;
};

const getYesterdayDate = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
};

const populateAgentTokens = (map: Record<string, string>, data: IAgentQuoteData) => {
  for (const [key, value] of Object.entries(data)) {
    if (value && value.length > 0) {
      map[key] = key === 'PriSubstat' ? StringHelper.formatStatCode(value) : value;
    } else {
      map[key] = (defaultAgentQuoteData as any)[key];
    }
  }
};

const populateAgencyTokens = (map: Record<string, string>, data: IAgencyMetrics) => {
  for (const [key, value] of Object.entries(data)) {
    if (value && (value as any).length > 0) {
      map[key] =
        key === 'OWNER_MASTER_SAFECO_AGENCY_CD' || key === 'SUBSTAT'
          ? StringHelper.formatStatCode(value as any)
          : (value as any);
    } else {
      map[key] = (defaultAgencyQuoteData as any)[key];
    }
  }
};

// Users belong to at most one of these mutually-exclusive campaign groups (per the data
// team), so filtering the campaigns by these names yields zero or one campaign.
const allowedCampaignNames = ['PL_Next_Level_Agency', 'PL_Hybrid'];

const populateCampaignTokens = (map: Record<string, string>, campaigns: Record<string, any>[]) => {
  // Pick the single campaign the portal cares about, then expose each of its fields as a
  // `UC_`-prefixed (User Campaign) token — e.g. {UC_CampaignName} — so campaign values
  // never overwrite other tokens (agent, agency, name, etc.) that may share a field name.
  const campaign = campaigns.find((c) => allowedCampaignNames.includes(c?.CampaignName));
  if (!campaign) return;

  for (const [key, rawValue] of Object.entries(campaign)) {
    map[`UC_${key}`] =
      rawValue === null || rawValue === undefined
        ? ''
        : typeof rawValue === 'string'
          ? rawValue
          : String(rawValue);
  }
};

const isValidDate = (dateString: string): boolean => {
  const date = new Date(dateString);
  return !isNaN(date.getTime());
};

// Pre-build state abbreviation map for O(1) lookup
const stateMap: Record<string, string> = Array.isArray(states)
  ? (states as any[]).reduce(
      (acc, s: any) => {
        if (s?.Abbreviation) acc[s.Abbreviation] = s.Name;
        return acc;
      },
      {} as Record<string, string>,
    )
  : {};

const formatFunctionalTokens = (input: string): string => {
  let doc = input;

  // Date tokens: $format_date_{variant}(date)
  const dateOptions: Record<string, Intl.DateTimeFormatOptions> = {
    full: { year: 'numeric', month: 'long', day: 'numeric' },
    simple: { year: 'numeric', month: 'long' },
    abbrev: { year: 'numeric', month: 'short' },
    regular: {},
  };
  doc = doc.replace(/\$format_date_(full|simple|abbrev|regular)\(([^()]*)\)/g, (m, variant: string, raw: string) => {
    const trimmed = raw.trim();
    if (!isValidDate(trimmed)) return '';
    return new Date(trimmed).toLocaleDateString('en-US', dateOptions[variant]);
  });

  // Percent tokens: $percentage(value) | $percentage_precise(value)
  doc = doc.replace(/\$percentage(_precise)?\(([^()]*)\)/g, (m, precise: string, raw: string) => {
    const num = raw.trim();
    if (!/^\d*\.?\d*$/.test(num)) return '';
    const val = parseFloat(num);
    if (isNaN(val)) return '';
    return val.toLocaleString(undefined, {
      style: 'percent',
      minimumFractionDigits: precise ? 1 : 0,
    });
  });

  // State tokens: $state(XX)
  doc = doc.replace(/\$state\(([^()]*)\)/g, (m, abbr: string) => {
    const code = abbr.trim();
    if (!/^[A-Z]{2}$/.test(code)) return '';
    return stateMap[code] || '';
  });

  return doc;
};
